import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PaginatedResult,
  PurchaseReceiptDetail,
  PurchaseReceiptSummary,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';
import { inventoryKeys } from '@/modules/inventory/api/inventory.api';
import { purchaseOrderKeys } from './purchase-orders.api';

export interface PurchaseReceiptListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  supplierId?: string;
  branchId?: string;
  purchaseOrderId?: string;
  dateFrom?: string;
  dateTo?: string;
}

/** One lot of one ordered line arriving. A line without lot tracking sends no lot fields at all. */
export interface PurchaseReceiptLineInput {
  purchaseOrderLineId: string;
  quantityReceived: number;
  lotNumber?: string;
  expiryDate?: string;
  manufacturedDate?: string;
}

export const purchaseReceiptKeys = {
  all: ['purchase-receipts'] as const,
  list: (params: PurchaseReceiptListParams) =>
    [...purchaseReceiptKeys.all, 'list', params] as const,
  detail: (id: string) => [...purchaseReceiptKeys.all, 'detail', id] as const,
};

export function usePurchaseReceiptsQuery(
  params: PurchaseReceiptListParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: purchaseReceiptKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<PurchaseReceiptSummary>>('/purchasing/receipts', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.search?.trim() ? { search: params.search.trim() } : {}),
          ...(params.supplierId ? { supplierId: params.supplierId } : {}),
          ...(params.branchId ? { branchId: params.branchId } : {}),
          ...(params.purchaseOrderId ? { purchaseOrderId: params.purchaseOrderId } : {}),
          ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
          ...(params.dateTo ? { dateTo: params.dateTo } : {}),
        },
      }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** The deliveries counted in against one order, newest first. */
export function usePurchaseOrderReceiptsQuery(purchaseOrderId: string | undefined) {
  return useQuery({
    queryKey: purchaseReceiptKeys.list({ purchaseOrderId, pageSize: 50 }),
    queryFn: () =>
      api.get<PaginatedResult<PurchaseReceiptSummary>>(
        `/purchasing/orders/${purchaseOrderId ?? ''}/receipts`,
        { params: { pageSize: 50 } },
      ),
    enabled: Boolean(purchaseOrderId),
  });
}

export function usePurchaseReceiptQuery(id: string | undefined) {
  return useQuery({
    queryKey: purchaseReceiptKeys.detail(id ?? ''),
    queryFn: () => api.get<PurchaseReceiptDetail>(`/purchasing/receipts/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

/**
 * Counts a delivery in.
 *
 * One key per attempt: a double click or a retry after a lost response books the goods onto the
 * shelf once. The order's version travels with it, so a delivery entered against a screen someone
 * else has already moved on from is refused rather than applied.
 */
export function usePostPurchaseReceiptMutation() {
  const queryClient = useQueryClient();

  return useIdempotentMutation<
    PurchaseReceiptDetail,
    {
      purchaseOrderId: string;
      purchaseOrderVersion: number;
      supplierDeliveryReference?: string;
      lines: PurchaseReceiptLineInput[];
    }
  >(
    ({ purchaseOrderId, ...payload }, config) =>
      api.post<PurchaseReceiptDetail>(
        `/purchasing/orders/${purchaseOrderId}/receipts`,
        payload,
        config,
      ),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: purchaseReceiptKeys.all });
        void queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
        void queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      },
    },
  );
}
